'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type WhatsAppTopic = {
  /** Texto curto mostrado na opção (ex: nome do produto). */
  label: string;
  /** Mensagem já escrita que abre no WhatsApp. */
  message: string;
  /** Acrescenta o link da página atual à mensagem (ex: produto). */
  includeUrl?: boolean;
};

type WhatsAppContextValue = {
  topic: WhatsAppTopic | null;
  setTopic: (topic: WhatsAppTopic | null) => void;
};

const WhatsAppContext = createContext<WhatsAppContextValue>({
  topic: null,
  setTopic: () => undefined,
});

export function WhatsAppProvider({ children }: { children: React.ReactNode }) {
  const [topic, setTopic] = useState<WhatsAppTopic | null>(null);
  const value = useMemo(() => ({ topic, setTopic }), [topic]);

  return (
    <WhatsAppContext.Provider value={value}>
      {children}
    </WhatsAppContext.Provider>
  );
}

export function useWhatsApp() {
  return useContext(WhatsAppContext);
}

/**
 * Uma página (produto, encomenda) diz ao botão do WhatsApp de que se trata,
 * para a mensagem já ir escrita. Limpa ao sair da página.
 */
export function useWhatsAppTopic(topic: WhatsAppTopic | null) {
  const { setTopic } = useWhatsApp();
  const label = topic?.label;
  const message = topic?.message;
  const includeUrl = topic?.includeUrl;

  useEffect(() => {
    if (!label || !message) return;
    setTopic({ label, message, includeUrl });
    return () => setTopic(null);
  }, [label, message, includeUrl, setTopic]);
}
